// Shared image upload pipeline for all photo inputs in the app.
// - Compresses client-side (~1600px longest edge, JPEG) BEFORE upload so we
//   don't ship 8 MB phone photos over the wire.
// - POSTs to /api/upload/image, which will resize a second time to a full +
//   thumbnail variant and return absolute URLs.
// - Returns { url, thumb_url } — callers should store URLs in the model
//   (never base64) so document size stays flat.
import imageCompression from 'browser-image-compression';
import api from '../api';

const MAX_INPUT_MB = 5;

/**
 * Compress + upload one image file. Falls back to the original file if
 * compression fails (e.g. exotic format the client can't decode).
 * @param {File} file
 * @returns {Promise<{ url: string, thumb_url: string, filename: string }>}
 */
export async function uploadImage(file) {
  if (!file) throw new Error('No file provided');
  if (file.size > MAX_INPUT_MB * 1024 * 1024) {
    throw new Error(`Image must be under ${MAX_INPUT_MB}MB`);
  }

  let toSend = file;
  try {
    toSend = await imageCompression(file, {
      maxSizeMB: 1,
      maxWidthOrHeight: 1600,
      useWebWorker: true,
      initialQuality: 0.82,
      fileType: file.type === 'image/png' ? 'image/png' : 'image/jpeg',
    });
  } catch {
    // Best-effort — if compression fails, upload the original.
    toSend = file;
  }

  const form = new FormData();
  form.append('file', toSend, toSend.name || file.name || 'upload');
  // Let axios set the multipart boundary automatically — overriding
  // Content-Type here strips the boundary and breaks some proxies.
  const res = await api.post('/upload/image', form);
  return res.data; // { url, thumb_url, filename, width, height }
}

/**
 * Given a photo URL produced by /api/upload/image (local disk or S3 layout
 * `/uploads/<name>` and `/uploads/thumbs/<name>`), return the thumbnail URL.
 * Anything that doesn't match the pipeline layout (base64 data URLs, preset
 * illustrations, external CDNs, empty values) is returned unchanged.
 *
 * Use this on LIST / feed / scroll surfaces so we send the 400px thumb
 * (~10× smaller) instead of the 1600px full variant.
 */
export function thumbFrom(url) {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('data:')) return url;                    // base64 legacy
  if (url.includes('/uploads/thumbs/')) return url;           // already a thumb
  // Matches BOTH `/api/static/uploads/foo.jpg` (dev disk) and any host that
  // ends with `/uploads/foo.jpg` (S3 public URL). Insert `thumbs/` right
  // before the filename segment.
  return url.replace(/\/uploads\/([^/?#]+)(\?|#|$)/, '/uploads/thumbs/$1$2');
}

/**
 * Return an `<img>` prop bag for LIST / feed / scroll surfaces:
 *   - `loading="lazy"` so below-the-fold images defer their fetch
 *   - `srcSet` offering the 400 px thumb + 1600 px full so retina displays
 *     picking through `sizes` can upgrade automatically
 *   - `sizes="128px"` matches every list slot in the app (avatars 16–48 px,
 *     card photos 96 px, community banners 128 px). At that hint the browser
 *     picks the 400w thumb in practice, so bandwidth stays flat.
 *
 * If the URL is not a pipeline URL (base64 legacy, preset illustration,
 * external CDN), only `src` + `loading` are emitted — no srcset.
 *
 * Usage:  `<img alt="..." className="..." {...feedImageProps(url)} />`
 */
export function feedImageProps(url) {
  if (!url) return { loading: 'lazy' };
  const thumb = thumbFrom(url);
  if (thumb === url) {
    return { src: url, loading: 'lazy' };
  }
  return {
    src: thumb,
    srcSet: `${thumb} 400w, ${url} 1600w`,
    sizes: '128px',
    loading: 'lazy',
  };
}
